import Link from '@/components/compat/Link';
import {
  painelDescriptionClass,
  painelPermissionItemEnabledClass,
  painelPermissionsModalShellClass,
} from '@/components/painel/painel-styles';
import { ArrowRight, ChevronDown, ShieldCheck, Users, type LucideIcon } from 'lucide-react';
import { cn } from '@/components/help-center/latest-power-shared';
import { MaskedSvgIcon } from '@/components/help-center/MaskedSvgIcon';

export type EquipeSubmenuItem = {
  href: string;
  label: string;
  description: string;
  icon: LucideIcon;
  /** SVG estático de public/icons — tem prioridade sobre `icon`. */
  iconSrc?: string;
};

export const EQUIPE_SUBMENU_ITEMS: EquipeSubmenuItem[] = [
  {
    href: '/equipe',
    label: 'Nossa Equipe',
    description: 'Conheça quem faz parte do projeto e mantém o xat Ajuda.',
    icon: Users,
    iconSrc: '/icons/equipe.svg',
  },
  {
    href: '/conduta',
    label: 'Código de Conduta',
    description: 'Entenda como nossa equipe trabalha e quais princípios seguimos.',
    icon: ShieldCheck,
  },
];

const ITEM_ICON_VISUALS: Record<
  string,
  { shell: string; icon: string; glow: string }
> = {
  '/equipe': {
    shell:
      'bg-gradient-to-br from-sky-500/25 to-sky-700/10 ring-1 ring-sky-400/35',
    icon: 'text-sky-200',
    glow: 'shadow-[0_0_40px_-10px_rgba(56,189,248,0.35)]',
  },
  '/conduta': {
    shell:
      'bg-gradient-to-br from-sky-500/25 to-sky-700/10 ring-1 ring-sky-400/35',
    icon: 'text-sky-200',
    glow: 'shadow-[0_0_40px_-10px_rgba(56,189,248,0.35)]',
  },
};

const panelShellClass = cn(painelPermissionsModalShellClass, 'relative py-2.5');

const desktopPanelClass = cn(panelShellClass, 'w-[360px]');

const mobilePanelClass = cn(panelShellClass, 'w-full py-2');

function EquipeDropdownPanel({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn(className)} role="menu">
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#030023]/40 via-[#000b6a]/20 to-[#030023]/50"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-[radial-gradient(ellipse_80%_55%_at_50%_-20%,rgba(56,189,248,0.12),transparent)]"
        aria-hidden
      />
      <div className="relative">{children}</div>
    </div>
  );
}

function EquipeSubmenuItemRow({
  item,
  active,
  onNavigate,
}: {
  item: EquipeSubmenuItem;
  active: boolean;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  const visuals = ITEM_ICON_VISUALS[item.href];

  return (
    <Link
      href={item.href}
      role="menuitem"
      onClick={onNavigate}
      className={cn(
        'group/item mx-1.5 flex items-center gap-3.5 rounded-xl border px-3 py-3.5',
        'transition-all duration-200 ease-out',
        active
          ? painelPermissionItemEnabledClass
          : 'border-transparent hover:border-sky-400/20 hover:bg-blue-950/45'
      )}
    >
      <span
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl transition-all duration-200',
          visuals?.shell,
          'group-hover/item:scale-[1.03]',
          active && visuals?.glow
        )}
      >
        {item.iconSrc ? (
          <MaskedSvgIcon
            src={item.iconSrc}
            className={cn(
              'h-[18px] w-[18px] transition-colors duration-200',
              visuals?.icon ?? 'text-slate-200',
              'group-hover/item:text-white',
              active && 'text-white'
            )}
          />
        ) : (
          <Icon
            className={cn(
              'h-[18px] w-[18px] transition-colors duration-200',
              visuals?.icon ?? 'text-slate-200',
              'group-hover/item:text-white',
              active && 'text-white'
            )}
            aria-hidden
          />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <p className="font-poppins text-[15px] font-semibold leading-snug text-slate-100">
          {item.label}
        </p>
        <p
          className={cn(
            'mt-0.5 font-inter text-[13px] leading-snug',
            painelDescriptionClass
          )}
        >
          {item.description}
        </p>
      </div>

      <ArrowRight
        className={cn(
          'h-4 w-4 shrink-0 text-sky-300/40 transition-all duration-200',
          'group-hover/item:translate-x-[3px] group-hover/item:text-sky-200/80',
          active && 'text-sky-200/70'
        )}
        aria-hidden
      />
    </Link>
  );
}

function EquipeSubmenuItems({
  pathname,
  onNavigate,
}: {
  pathname: string;
  onNavigate?: () => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      {EQUIPE_SUBMENU_ITEMS.map((item) => (
        <EquipeSubmenuItemRow
          key={item.href}
          item={item}
          active={pathname === item.href}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
}

export function EquipeDesktopDropdown({ pathname }: { pathname: string }) {
  const equipeActive = pathname === '/equipe' || pathname === '/conduta';

  return (
    <div className="relative group">
      <button
        type="button"
        className={cn(
          'flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 hover:bg-white/20 font-poppins',
          equipeActive && 'bg-white/15'
        )}
        aria-haspopup="menu"
        aria-expanded={equipeActive}
      >
        <MaskedSvgIcon src="/icons/equipe.svg" className="h-5 w-5" />
        Equipe
        <ChevronDown className="h-3.5 w-3.5 opacity-70 transition-transform duration-200 group-hover:rotate-180" />
      </button>

      <div
        className={cn(
          'absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3',
          'pointer-events-none origin-top',
          'translate-y-2 scale-[0.98] opacity-0',
          'transition-all duration-[180ms] ease-out',
          'group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100',
          'group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:scale-100 group-focus-within:opacity-100'
        )}
      >
        <EquipeDropdownPanel className={desktopPanelClass}>
          <EquipeSubmenuItems pathname={pathname} />
        </EquipeDropdownPanel>
      </div>
    </div>
  );
}

export function EquipeMobileSection({
  pathname,
  open,
  onToggle,
  onNavigate,
}: {
  pathname: string;
  open: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  return (
    <div className="py-1">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between py-2 text-sm font-medium text-white transition-colors hover:text-blue-100 font-poppins"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          <MaskedSvgIcon src="/icons/equipe.svg" className="h-5 w-5" />
          Equipe
        </span>
        <ChevronDown
          className={cn('h-4 w-4 transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      <div
        className={cn(
          'grid transition-all duration-[180ms] ease-out',
          open ? 'mt-3 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <EquipeDropdownPanel className={mobilePanelClass}>
            <EquipeSubmenuItems pathname={pathname} onNavigate={onNavigate} />
          </EquipeDropdownPanel>
        </div>
      </div>
    </div>
  );
}

export function isEquipeNavActive(pathname: string) {
  return pathname === '/equipe' || pathname === '/conduta';
}
